import { BvMsgBoxOptions } from "bootstrap-vue";
import Vue, { VNode } from "vue";

import { NativeAppPage } from "@/plugins/native-app";

export function toVNodes(vue: Vue, html: string) {
  const vnode = vue.$createElement("div", { domProps: { innerHTML: html } });
  return [vnode];
}

function show(vue: Vue, method: "msgBoxOk" | "msgBoxConfirm", message: string | VNode[], options?: BvMsgBoxOptions) {
  // The current version of Bootstrap-Vue does not support setting a default value for noCloseOnBackdrop, so set it
  // here instead. On mobile devices, it's easy to accidentally tap the screen and dismiss a modal (especially when
  // performing biometrics)
  if (!options) options = {};
  options.noCloseOnBackdrop = true;

  return vue.$bvModal[method](message, options);
}

function showHtmlMsgBoxOk(vue: Vue, html: string, options?: BvMsgBoxOptions): Promise<void> {
  return show(vue, "msgBoxOk", toVNodes(vue, html), options);
}

function showHtmlMsgBoxConfirm(vue: Vue, html: string, options?: BvMsgBoxOptions): Promise<boolean | undefined | null> {
  return show(vue, "msgBoxConfirm", toVNodes(vue, html), options);
}

const Modals = {
  showOk: (vue: Vue, message: string, options?: BvMsgBoxOptions): Promise<void> =>
    show(vue, "msgBoxOk", message, options),
  showConfirm: (vue: Vue, message: string, options?: BvMsgBoxOptions): Promise<boolean | undefined | null> =>
    show(vue, "msgBoxConfirm", message, options),

  biometric: {
    check: {
      showNoSharedDevicesWarning: (vue: Vue) =>
        showHtmlMsgBoxOk(vue, "Do not use biometrics on shared devices.", { title: "Warning" }),
      showNoHardware: (vue: Vue) =>
        showHtmlMsgBoxOk(vue, "Your device does not support biometric authentication.", {
          title: "Biometrics Not Supported"
        }),
      showNotConfigured: async (vue: Vue) => {
        const message = `Please setup biometrics on your device.<br />
        <br />
        Do not use biometrics on shared devices.`;
        const options: BvMsgBoxOptions = { title: "Biometrics Not Configured" };

        // If the native app supports navigating to the biometrics settings page, show a modal where the secondary
        // button can be used to open the biometrics settings
        if (vue.$nativeApp.canNavigateTo(NativeAppPage.BiometricSettings)) {
          options.cancelTitle = "Setup Biometrics";

          const result = await showHtmlMsgBoxConfirm(vue, message, options);

          // true = OK, false = Setup Biometrics, null/undefined = Dismissed (e.g. X or ESC)
          if (result === false) {
            vue.$nativeApp.navigateTo(NativeAppPage.BiometricSettings);
          }
        } else {
          await showHtmlMsgBoxOk(vue, message, options);
        }
      },
      showNoPermission: (vue: Vue) =>
        showHtmlMsgBoxOk(
          vue,
          `Please enable biometric permissions on your device for this application.<br />
          <br />
          Do not use biometrics on shared devices.`,
          { title: "Need Permissions" }
        ),
      showUnknownError: (vue: Vue) =>
        showHtmlMsgBoxOk(vue, "An unexpected error occurred and biometric authentication could not be configured.", {
          title: "Error"
        })
    },

    setup: {
      showNotConfigured: (vue: Vue) =>
        showHtmlMsgBoxOk(
          vue,
          `Biometric authentication could not be enabled because your device does not have any biometrics configured.<br />
          <br />
          You can setup your device's biometrics and try again the next time you sign in.`,
          { title: "No Device Biometrics" }
        ),
      showNoPermission: (vue: Vue) =>
        showHtmlMsgBoxOk(
          vue,
          `Biometric authentication could not be enabled because this application has been denied access to your device's biometrics.<br />
          <br />
          You can enable biometric permissions for this application and try again the next time you sign in.`,
          { title: "Need Permissions" }
        ),
      showFailed: (vue: Vue) =>
        showHtmlMsgBoxOk(
          vue,
          `Biometric authentication could not be enabled because you've had too many failed biometric attempts.<br />
          <br />
          You can try again the next time you sign in.`,
          { title: "Too Many Attempts" }
        ),
      showLockedOut: (vue: Vue) =>
        showHtmlMsgBoxOk(
          vue,
          `Biometric authentication could not be enabled because your device's biometrics are locked.<br />
          <br />
          You can unlock your biometrics by locking and unlocking your device.<br />
          <br />
          You can try again the next time you sign in.`,
          { title: "Biometrics Locked" }
        ),
      showUnknownError: (vue: Vue) =>
        showHtmlMsgBoxOk(
          vue,
          `There was an issue trying to setup up biometric authentication.<br />
          <br />
          Please try again later.`,
          { title: "Error" }
        )
    },

    login: {
      showBiometricsChanged: (vue: Vue) =>
        showHtmlMsgBoxOk(
          vue,
          `The biometrics on your device have changed.<br />
          <br />
          Please sign in with your email and password to setup your biometric authentication again.`,
          { title: "Biometrics Changed" }
        ),
      showBiometricsNotConfigured: (vue: Vue) =>
        showHtmlMsgBoxOk(
          vue,
          `Your device does not have any biometrics configured.<br />
          <br />
          Please setup your device's biometrics and try again.`,
          { title: "No Device Biometrics" }
        ),
      showNoPermission: (vue: Vue) =>
        showHtmlMsgBoxOk(
          vue,
          `This application needs permission to perform biometric authentication.<br />
          <br />
          Please enable biometric permissions for this application and try again.`,
          { title: "Need Permissions" }
        ),
      showFailed: (vue: Vue) =>
        showHtmlMsgBoxOk(
          vue,
          `You've had too many failed biometric attempts.<br />
          <br />
          Please try again later.`,
          { title: "Too Many Attempts" }
        ),
      showLockedOut: (vue: Vue) =>
        showHtmlMsgBoxOk(
          vue,
          `Your device's biometrics have been locked.<br />
          <br />
          You can unlock your biometrics by locking and then unlocking your device.`,
          { title: "Biometrics Locked" }
        ),
      showBiometricsNotMatching: (vue: Vue) =>
        showHtmlMsgBoxOk(
          vue,
          `There was an issue with your biometrics.<br />
          <br />
          Please sign in with your email and password to setup your biometric authentication again.`,
          { title: "Failed Signing In" }
        ),
      showUnknownError: (vue: Vue) =>
        showHtmlMsgBoxOk(
          vue,
          `An unexpected error occurred while trying to sign in with biometrics.<br />
          <br />
          Please log in with your email and password and try again later.`,
          { title: "Failed Signing In" }
        )
    },

    remove: {
      showConfirmation: (vue: Vue) =>
        showHtmlMsgBoxConfirm(
          vue,
          `You will no longer be able to login with your device's biometrics and will have to use your email and password.<br />
          <br />
          Remove biometric authentication?`,
          { title: "Remove Biometrics?", okTitle: "Remove", okVariant: "danger" }
        )
    }
  },

  login: {
    showPasswordExpiringSoonAndNavigateAway: async (vue: Vue, days: number, redirectTo: string) => {
      const dayOrDays = days === 1 ? "day" : "days";
      const proceed = await showHtmlMsgBoxConfirm(
        vue,
        `Your password will expire in ${days} ${dayOrDays}. Please change your password to avoid being locked out.`,
        { title: "Password Expiring", okTitle: "Change Password", cancelTitle: "Later" }
      );

      if (proceed) {
        vue.$router.push({ path: "/change-password", query: { redirectTo } });
      } else {
        vue.$router.push(redirectTo);
      }
    },
    showPasswordExpiringTodayAndNavigateToChangePassword: async (vue: Vue, redirectTo: string) => {
      await showHtmlMsgBoxOk(
        vue,
        `Your password will expire today. Please change your password to avoid being locked out tomorrow.`,
        { title: "Password Expiring" }
      );

      vue.$router.push({ path: "/change-password", query: { redirectTo } });
    }
  },

  trustedDevice: {
    add: {
      showUnknownError: (vue: Vue) =>
        showHtmlMsgBoxOk(
          vue,
          `There was an issue trying to save your device as trusted.<br />
          <br />
          Please try again later.`,
          { title: "Error" }
        )
    }
  }
};

export default Modals;
